import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Envelope, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-surface">
      <div className="w-full max-w-md space-y-6">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-accent-brand" data-testid="back-to-login-link">
          <ArrowLeft size={16} /> Back to login
        </Link>

        {sent ? (
          <div className="bg-white border border-border-default rounded p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50">
              <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">Check your email</h2>
            <p className="text-text-secondary">
              If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset link.
              It will expire in 30 minutes.
            </p>
            <p className="text-xs text-text-tertiary">
              Didn&apos;t receive it? Ask an admin — they can view pending reset links in the Admin dashboard.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full mt-2" data-testid="return-to-login-button">
                Return to login
              </Button>
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-border-default rounded p-8 space-y-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-text-primary">
                Forgot password?
              </h2>
              <p className="mt-2 text-text-secondary">
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fp-email">Email Address</Label>
                <div className="relative">
                  <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-11"
                    placeholder="your.email@company.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-accent-brand hover:bg-accent-brand-hover text-white"
                data-testid="forgot-submit-button"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
